import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function SignUpPage() {
  const [scrolled, setScrolled] = useState(false);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const formData = new FormData(e.currentTarget);
    const data = new URLSearchParams();
    
    formData.forEach((value, key) => {
      data.append(key, value.toString());
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d5lf5n0lhqshf1uh06tg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: data.toString()
      });

      if (response.ok) {
        setFormStatus('success');
        e.currentTarget.reset();
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus('idle'), 5000);
      }
    } catch (error) {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 5000);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Page-level SEO */}
      <title>Sign Up – New Patient Intake | CorexMed Executive Medicine Miramar Florida</title>
      <meta name="description" content="Join CorexMed as a new patient. Complete your intake form to begin your personalized executive medicine journey with Dr. David Coradin in Miramar, Florida. 24/7 concierge access, longevity medicine & hormone optimization." />
      <link rel="canonical" href={`${import.meta.env.VITE_SITE_URL ?? 'https://example.com'}/signup`} />
      <script type="application/ld+json">{JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "Sign Up – New Patient Intake | CorexMed",
        "description": "New patient intake form for CorexMed executive medicine practice. Join Dr. David Coradin's concierge medicine practice in Miramar, Florida.",
        "url": `${import.meta.env.VITE_SITE_URL ?? 'https://example.com'}/signup`,
        "isPartOf": {
          "@type": "WebSite",
          "name": "CorexMed",
          "url": import.meta.env.VITE_SITE_URL ?? 'https://example.com'
        },
        "breadcrumb": {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": import.meta.env.VITE_SITE_URL ?? 'https://example.com' },
            { "@type": "ListItem", "position": 2, "name": "Sign Up", "item": `${import.meta.env.VITE_SITE_URL ?? 'https://example.com'}/signup` }
          ]
        }
      })}</script>
      <script type="application/ld+json">{JSON.stringify({
        "@context": "https://schema.org",
        "@type": "MedicalClinic",
        "name": "CorexMed",
        "description": "Concierge executive medicine practice offering personalized longevity medicine, hormone optimization, AI cardiac testing, and comprehensive preventive care.",
        "url": import.meta.env.VITE_SITE_URL ?? 'https://example.com',
        "telephone": "+1-954-555-0100",
        "email": "info@corexmed.com",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "12345 Executive Drive",
          "addressLocality": "Miramar",
          "addressRegion": "FL",
          "postalCode": "33025",
          "addressCountry": "US"
        },
        "availableService": [
          { "@type": "MedicalTherapy", "name": "Longevity Medicine" },
          { "@type": "MedicalTherapy", "name": "Hormone Optimization Therapy" },
          { "@type": "MedicalTherapy", "name": "Peptide Therapy" },
          { "@type": "MedicalTherapy", "name": "AI-Powered Cardiac CT Angiography" },
          { "@type": "MedicalTherapy", "name": "Regenerative Medicine" },
          { "@type": "MedicalTherapy", "name": "DNA Testing & Genetic Analysis" }
        ]
      })}</script>

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-white shadow-sm'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <h1 className="text-2xl font-bold tracking-tight text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>
              CorexMed
            </h1>
          </Link>
          <div className="hidden md:flex items-center space-x-8">
            <Link to="/#services" className="text-sm font-medium text-gray-700 hover:text-amber-600 transition-colors">Services</Link>
            <Link to="/#about" className="text-sm font-medium text-gray-700 hover:text-amber-600 transition-colors">About Dr. Coradin</Link>
            <Link to="/#testing" className="text-sm font-medium text-gray-700 hover:text-amber-600 transition-colors">Testing</Link>
            <Link to="/education" className="text-sm font-medium text-gray-700 hover:text-amber-600 transition-colors">Education</Link>
            <a href="#portal" className="px-5 py-2 rounded-full border-2 border-gray-900 text-gray-900 text-sm font-medium whitespace-nowrap hover:bg-gray-900 hover:text-white transition-all cursor-pointer">
              Patient Portal
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-gray-900 via-slate-800 to-slate-900 overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,.05) 35px, rgba(255,255,255,.05) 70px)' }}></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/30"></div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <div className="inline-block px-4 py-2 bg-amber-600/20 border border-amber-600/30 text-amber-400 rounded-full text-xs font-semibold mb-6">
            NEW PATIENT INTAKE
          </div>
          <h1 className="text-6xl md:text-7xl font-bold text-white mb-6 leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
            Sign Up
          </h1>
          <p className="text-xl text-white/80 leading-relaxed max-w-2xl mx-auto">
            Begin your journey to exceptional health. Complete your intake information and join the CorexMed family.
          </p>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-amber-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              What You'll Receive as a CorexMed Member
            </h2>
          </div>
          
          <div className="grid md:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-sm text-center">
              <div className="w-14 h-14 bg-amber-100 rounded-lg flex items-center justify-center mb-4 mx-auto">
                <i className="ri-phone-line text-2xl text-amber-700"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">24/7 Direct Access</h3>
              <p className="text-sm text-gray-600">Dr. Coradin's personal line, anytime you need</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm text-center">
              <div className="w-14 h-14 bg-amber-100 rounded-lg flex items-center justify-center mb-4 mx-auto">
                <i className="ri-test-tube-line text-2xl text-amber-700"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Comprehensive Testing</h3>
              <p className="text-sm text-gray-600">Advanced diagnostics beyond standard care</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm text-center">
              <div className="w-14 h-14 bg-amber-100 rounded-lg flex items-center justify-center mb-4 mx-auto">
                <i className="ri-user-heart-line text-2xl text-amber-700"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Personalized Plans</h3>
              <p className="text-sm text-gray-600">Tailored protocols for your unique biology</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm text-center">
              <div className="w-14 h-14 bg-amber-100 rounded-lg flex items-center justify-center mb-4 mx-auto">
                <i className="ri-team-line text-2xl text-amber-700"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Specialist Network</h3>
              <p className="text-sm text-gray-600">Coordinated access to top specialists</p>
            </div>
          </div>
        </div>
      </section>

      {/* Intake Form Section */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Patient Intake Information
            </h2>
            <p className="text-lg text-gray-600">
              Please provide the following information to begin your membership
            </p>
          </div>

          <form 
            data-readdy-form 
            id="patient-intake-signup"
            onSubmit={handleSubmit}
            className="bg-gray-50 p-8 md:p-12 rounded-2xl shadow-lg"
          >
            {/* Personal Information */}
            <div className="mb-10">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <div className="w-8 h-8 bg-amber-600 text-white rounded-full flex items-center justify-center text-sm font-bold">1</div>
                Personal Information
              </h3>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    First Name <span className="text-red-600">*</span>
                  </label>
                  <input 
                    type="text"
                    name="first_name"
                    required
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-amber-600 focus:ring-2 focus:ring-amber-600/20"
                    placeholder="John"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Last Name <span className="text-red-600">*</span>
                  </label>
                  <input 
                    type="text"
                    name="last_name"
                    required
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-amber-600 focus:ring-2 focus:ring-amber-600/20"
                    placeholder="Smith"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Date of Birth <span className="text-red-600">*</span>
                  </label>
                  <input 
                    type="date"
                    name="date_of_birth"
                    required
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-amber-600 focus:ring-2 focus:ring-amber-600/20"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Gender <span className="text-red-600">*</span>
                  </label>
                  <select 
                    name="gender"
                    required
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-amber-600 focus:ring-2 focus:ring-amber-600/20"
                  >
                    <option value="">Select gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                    <option value="Prefer not to say">Prefer not to say</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="mb-10">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <div className="w-8 h-8 bg-amber-600 text-white rounded-full flex items-center justify-center text-sm font-bold">2</div>
                Contact Information
              </h3>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address <span className="text-red-600">*</span>
                  </label>
                  <input 
                    type="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-amber-600 focus:ring-2 focus:ring-amber-600/20"
                    placeholder="john.smith@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Phone Number <span className="text-red-600">*</span>
                  </label>
                  <input 
                    type="tel"
                    name="phone"
                    required
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-amber-600 focus:ring-2 focus:ring-amber-600/20"
                    placeholder="(555) 123-4567"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Preferred Contact Method <span className="text-red-600">*</span>
                  </label>
                  <select 
                    name="preferred_contact"
                    required
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-amber-600 focus:ring-2 focus:ring-amber-600/20"
                  >
                    <option value="">Select method</option>
                    <option value="Phone">Phone</option>
                    <option value="Email">Email</option>
                    <option value="Text">Text Message</option>
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Street Address <span className="text-red-600">*</span>
                  </label>
                  <input 
                    type="text"
                    name="street_address"
                    required
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-amber-600 focus:ring-2 focus:ring-amber-600/20"
                    placeholder="123 Main Street"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    City <span className="text-red-600">*</span>
                  </label>
                  <input 
                    type="text"
                    name="city"
                    required
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-amber-600 focus:ring-2 focus:ring-amber-600/20"
                    placeholder="Miami"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    State <span className="text-red-600">*</span>
                  </label>
                  <input 
                    type="text"
                    name="state"
                    required
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-amber-600 focus:ring-2 focus:ring-amber-600/20"
                    placeholder="FL"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    ZIP Code <span className="text-red-600">*</span>
                  </label>
                  <input 
                    type="text"
                    name="zip_code"
                    required
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-amber-600 focus:ring-2 focus:ring-amber-600/20"
                    placeholder="33025"
                  />
                </div>
              </div>
            </div>

            {/* Medical History */}
            <div className="mb-10">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <div className="w-8 h-8 bg-amber-600 text-white rounded-full flex items-center justify-center text-sm font-bold">3</div>
                Medical History
              </h3>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Current Medications
                  </label>
                  <textarea 
                    name="current_medications"
                    rows={3}
                    maxLength={500}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-amber-600 focus:ring-2 focus:ring-amber-600/20"
                    placeholder="List all current medications, dosages, and frequency (max 500 characters)"
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Known Allergies
                  </label>
                  <textarea 
                    name="allergies"
                    rows={2}
                    maxLength={500}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-amber-600 focus:ring-2 focus:ring-amber-600/20"
                    placeholder="List any medication or food allergies (max 500 characters)"
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Chronic Conditions
                  </label>
                  <textarea 
                    name="chronic_conditions"
                    rows={3}
                    maxLength={500}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-amber-600 focus:ring-2 focus:ring-amber-600/20"
                    placeholder="List any chronic health conditions (diabetes, hypertension, etc.) (max 500 characters)"
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Previous Surgeries
                  </label>
                  <textarea 
                    name="previous_surgeries"
                    rows={3}
                    maxLength={500}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-amber-600 focus:ring-2 focus:ring-amber-600/20"
                    placeholder="List any previous surgeries and approximate dates (max 500 characters)"
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Family Medical History
                  </label>
                  <textarea 
                    name="family_history"
                    rows={3}
                    maxLength={500}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-amber-600 focus:ring-2 focus:ring-amber-600/20"
                    placeholder="Notable family health conditions (heart disease, cancer, diabetes, etc.) (max 500 characters)"
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
                </div>
              </div>
            </div>

            {/* Health Goals */}
            <div className="mb-10">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <div className="w-8 h-8 bg-amber-600 text-white rounded-full flex items-center justify-center text-sm font-bold">4</div>
                Health Goals & Interests
              </h3>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Primary Health Goals <span className="text-red-600">*</span>
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input type="checkbox" name="health_goals" value="Longevity & Anti-Aging" className="w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-600" />
                      <span className="text-sm text-gray-700">Longevity & Anti-Aging</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input type="checkbox" name="health_goals" value="Hormone Optimization" className="w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-600" />
                      <span className="text-sm text-gray-700">Hormone Optimization</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input type="checkbox" name="health_goals" value="Weight Management" className="w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-600" />
                      <span className="text-sm text-gray-700">Weight Management</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input type="checkbox" name="health_goals" value="Cardiovascular Health" className="w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-600" />
                      <span className="text-sm text-gray-700">Cardiovascular Health</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input type="checkbox" name="health_goals" value="Performance Optimization" className="w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-600" />
                      <span className="text-sm text-gray-700">Performance Optimization</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input type="checkbox" name="health_goals" value="Chronic Disease Management" className="w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-600" />
                      <span className="text-sm text-gray-700">Chronic Disease Management</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input type="checkbox" name="health_goals" value="Preventive Care" className="w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-600" />
                      <span className="text-sm text-gray-700">Preventive Care</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Services of Interest
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input type="checkbox" name="services_interest" value="Peptide Therapy" className="w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-600" />
                      <span className="text-sm text-gray-700">Peptide Therapy</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input type="checkbox" name="services_interest" value="Regenerative Medicine" className="w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-600" />
                      <span className="text-sm text-gray-700">Regenerative Medicine</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input type="checkbox" name="services_interest" value="AI Cardiac Testing" className="w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-600" />
                      <span className="text-sm text-gray-700">AI-Powered Cardiac CT Angiography</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input type="checkbox" name="services_interest" value="DNA Testing" className="w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-600" />
                      <span className="text-sm text-gray-700">DNA Testing & Genetic Analysis</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input type="checkbox" name="services_interest" value="Nutritional Optimization" className="w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-600" />
                      <span className="text-sm text-gray-700">Nutritional Optimization</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Additional Comments or Questions
                  </label>
                  <textarea 
                    name="additional_comments"
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-amber-600 focus:ring-2 focus:ring-amber-600/20"
                    placeholder="Share any additional information or questions you have about CorexMed services (max 500 characters)"
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
                </div>
              </div>
            </div>

            {/* Insurance Information */}
            <div className="mb-10">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <div className="w-8 h-8 bg-amber-600 text-white rounded-full flex items-center justify-center text-sm font-bold">5</div>
                Insurance Information
              </h3>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Insurance Provider
                  </label>
                  <input 
                    type="text"
                    name="insurance_provider"
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-amber-600 focus:ring-2 focus:ring-amber-600/20"
                    placeholder="e.g., Blue Cross Blue Shield"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Policy Number
                  </label>
                  <input 
                    type="text"
                    name="policy_number"
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-amber-600 focus:ring-2 focus:ring-amber-600/20"
                    placeholder="Policy number"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Group Number
                  </label>
                  <input 
                    type="text"
                    name="group_number"
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-amber-600 focus:ring-2 focus:ring-amber-600/20"
                    placeholder="Group number"
                  />
                </div>
              </div>
            </div>

            {/* Emergency Contact */}
            <div className="mb-10">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <div className="w-8 h-8 bg-amber-600 text-white rounded-full flex items-center justify-center text-sm font-bold">6</div>
                Emergency Contact
              </h3>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Emergency Contact Name <span className="text-red-600">*</span>
                  </label>
                  <input 
                    type="text"
                    name="emergency_contact_name"
                    required
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-amber-600 focus:ring-2 focus:ring-amber-600/20"
                    placeholder="Full name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Relationship <span className="text-red-600">*</span>
                  </label>
                  <input 
                    type="text"
                    name="emergency_contact_relationship"
                    required
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-amber-600 focus:ring-2 focus:ring-amber-600/20"
                    placeholder="e.g., Spouse, Parent, Sibling"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Emergency Contact Phone <span className="text-red-600">*</span>
                  </label>
                  <input 
                    type="tel"
                    name="emergency_contact_phone"
                    required
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-amber-600 focus:ring-2 focus:ring-amber-600/20"
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>
            </div>

            {/* Consent & Agreement */}
            <div className="mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <div className="w-8 h-8 bg-amber-600 text-white rounded-full flex items-center justify-center text-sm font-bold">7</div>
                Consent & Agreement
              </h3>
              
              <div className="space-y-4 bg-white p-6 rounded-lg border border-gray-300">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input 
                    type="checkbox" 
                    name="consent_treatment" 
                    value="Yes"
                    required
                    className="w-5 h-5 text-amber-600 border-gray-300 rounded focus:ring-amber-600 mt-0.5 flex-shrink-0" 
                  />
                  <span className="text-sm text-gray-700">
                    <strong>I consent to treatment</strong> and authorize CorexMed to provide medical care and services as deemed necessary by Dr. David Coradin. <span className="text-red-600">*</span>
                  </span>
                </label>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input 
                    type="checkbox" 
                    name="consent_privacy" 
                    value="Yes"
                    required
                    className="w-5 h-5 text-amber-600 border-gray-300 rounded focus:ring-amber-600 mt-0.5 flex-shrink-0" 
                  />
                  <span className="text-sm text-gray-700">
                    <strong>I acknowledge receipt of the Privacy Policy</strong> and understand how my health information will be used and protected under HIPAA regulations. <span className="text-red-600">*</span>
                  </span>
                </label>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input 
                    type="checkbox" 
                    name="consent_communication" 
                    value="Yes"
                    required
                    className="w-5 h-5 text-amber-600 border-gray-300 rounded focus:ring-amber-600 mt-0.5 flex-shrink-0" 
                  />
                  <span className="text-sm text-gray-700">
                    <strong>I consent to communication</strong> via phone, email, and text message for appointment reminders, test results, and health-related information. <span className="text-red-600">*</span>
                  </span>
                </label>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input 
                    type="checkbox" 
                    name="consent_telemedicine" 
                    value="Yes"
                    className="w-5 h-5 text-amber-600 border-gray-300 rounded focus:ring-amber-600 mt-0.5 flex-shrink-0" 
                  />
                  <span className="text-sm text-gray-700">
                    <strong>I consent to telemedicine services</strong> and understand the benefits and limitations of virtual healthcare consultations.
                  </span>
                </label>
              </div>
            </div>

            {/* Status Messages */}
            {formStatus === 'success' && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-green-600 text-xl mt-0.5"></i>
                <div>
                  <p className="font-semibold text-green-900">Application Submitted Successfully!</p>
                  <p className="text-sm text-green-700 mt-1">Thank you for choosing CorexMed. Our team will review your information and contact you within 24-48 hours to schedule your initial consultation.</p>
                </div>
              </div>
            )}

            {formStatus === 'error' && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <i className="ri-error-warning-fill text-red-600 text-xl mt-0.5"></i>
                <div>
                  <p className="font-semibold text-red-900">Submission Failed</p>
                  <p className="text-sm text-red-700 mt-1">There was an error submitting your application. Please try again or contact us directly.</p>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <div className="text-center">
              <button 
                type="submit"
                disabled={formStatus === 'submitting'}
                className="inline-flex items-center gap-3 px-12 py-4 bg-amber-600 text-white rounded-full font-bold text-base hover:bg-amber-700 transition-all shadow-lg whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {formStatus === 'submitting' ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Submitting...
                  </>
                ) : (
                  <>
                    Submit Application
                    <i className="ri-arrow-right-line"></i>
                  </>
                )}
              </button>
              <p className="text-sm text-gray-600 mt-4">
                By submitting this form, you agree to our Terms of Service and Privacy Policy
              </p>
            </div>
          </form>
        </div>
      </section>

      {/* Next Steps Section */}
      <section className="py-16 bg-gradient-to-br from-gray-900 via-slate-800 to-slate-900">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
            What Happens Next?
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-amber-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                1
              </div>
              <h3 className="text-white font-bold mb-2">Review</h3>
              <p className="text-sm text-gray-300">Our team reviews your intake information within 24-48 hours</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-amber-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                2
              </div>
              <h3 className="text-white font-bold mb-2">Contact</h3>
              <p className="text-sm text-gray-300">We'll reach out to schedule your initial consultation with Dr. Coradin</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-amber-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                3
              </div>
              <h3 className="text-white font-bold mb-2">Begin</h3>
              <p className="text-sm text-gray-300">Start your personalized health journey with comprehensive assessment</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white pt-16 pb-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <h4 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>CorexMed</h4>
              <p className="text-sm text-gray-400 leading-relaxed">
                Executive Medicine for Exceptional Lives
              </p>
            </div>
            
            <div>
              <h5 className="font-bold mb-4 text-base">Navigate</h5>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><Link to="/#about" className="hover:text-amber-600 transition-colors">About Dr. Coradin</Link></li>
                <li><Link to="/#services" className="hover:text-amber-600 transition-colors">Services</Link></li>
                <li><Link to="/#testing" className="hover:text-amber-600 transition-colors">Testing & Diagnostics</Link></li>
                <li><Link to="/education" className="hover:text-amber-600 transition-colors">Education</Link></li>
              </ul>
            </div>
            
            <div>
              <h5 className="font-bold mb-4 text-base">Contact</h5>
              <ul className="space-y-2 text-sm text-gray-400">
                <li className="flex items-center gap-2">
                  <i className="ri-phone-line"></i>
                  <span>(555) 123-4567</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-mail-line"></i>
                  <span>info@corexmed.com</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-map-pin-line mt-1"></i>
                  <span>12345 Executive Drive<br />Miramar, FL 33025</span>
                </li>
              </ul>
            </div>
            
            <div>
              <h5 className="font-bold mb-4 text-base">Follow Us</h5>
              <div className="flex gap-3">
                <a href="#linkedin" className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-amber-600 transition-all cursor-pointer">
                  <i className="ri-linkedin-fill"></i>
                </a>
                <a href="#instagram" className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-amber-600 transition-all cursor-pointer">
                  <i className="ri-instagram-line"></i>
                </a>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-500">
            <p>&copy; 2025 CorexMed. All rights reserved.</p>
            <div className="flex gap-6">
              <a href="#privacy" className="hover:text-amber-600 transition-colors cursor-pointer">Privacy Policy</a>
              <a href="#terms" className="hover:text-amber-600 transition-colors cursor-pointer">Terms of Service</a>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-amber-600 transition-colors">Powered by Readdy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
